import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { crmApi, tenantApi } from '../api/endpoints';
import { dashboardPath, filesPath, lastCompany, useCompanies, useCurrentCompany } from '../features/companies/hooks';
import { useI18n } from '../i18n';
import { Icon, type IconName } from '../ui';

// Adaptive navigation (TF-93, Material 3 window size classes). One list of destinations, drawn by the
// component that suits the window: a navigation bar at the bottom of a phone (compact, under 600px),
// a rail on a tablet (medium, 600-839), a drawer on a computer (840 and wider), which folds to a rail
// when its person wants. The rest of the destinations and the company switcher sit in a modal drawer
// behind More (bar) or the menu button (rail). Which component shows is decided by CSS, so exactly one
// navigation landmark is on the page at each size.

export interface Destination {
  key: string;
  to: string;
  icon: IconName;
  label: string;
  /** The bar's and the rail's shorter label. */
  shortLabel: string;
  /** Shown in the bar and the rail; the drawer shows all. */
  primary: boolean;
  /** Where the drawer groups it; null: the first group, without a heading. */
  section: 'customers' | 'company' | null;
  current: boolean;
  /** A count shown as a Material badge (overdue tasks). */
  badge?: number;
}

/** How many of my tasks are overdue, for the Tasks badge: asked every two minutes and on focus. */
function useOverdueTasks(tenantId: string | null): number {
  const overdue = useQuery({
    // A crm- key: every CRM change (a task done, a due date moved) refreshes it too (useCrmRefresh).
    queryKey: ['crm-overdue-tasks', tenantId],
    queryFn: () => crmApi.overdueCount(tenantId!),
    enabled: tenantId !== null,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
  return overdue.data ?? 0;
}

/** The destinations this person has, marked current by the address. */
export function useDestinations(): Destination[] {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const { tenantId, company, isMember } = useCurrentCompany();
  const companies = useCompanies();
  // Outside a company's pages (shared, account), the links lead to the last company used.
  const home = company ?? (tenantId ? null : lastCompany(companies.data ?? []) ?? null);
  // A guest (role external) has only what was shared with them (TF-93 criterion 7).
  const member = home !== null && home.role !== 'external' && (isMember || !tenantId);
  // Somebody from outside the company in its teams (TF-302): their teams' folders, and while the company's
  // access follows teams their teams' deals, accounts and contacts. Nothing else of the company.
  const outsider = home !== null && home.role === 'external';
  // Their list of teams holds only the teams they are in: one row says whether there are any.
  const outsideTeams = useQuery({
    queryKey: ['teams', home?.id, 'outside-nav'],
    queryFn: () => tenantApi.teams(home!.id, { limit: 1 }),
    enabled: outsider,
    staleTime: 60_000,
  });
  const inTeams = outsider && (outsideTeams.data?.teams.length ?? 0) > 0;
  const outsideRecords = inTeams && home.settings?.accessFollowsTeams === true;
  const overdue = useOverdueTasks(member ? home.id : null);
  const under = (path: string) => pathname === path || pathname.startsWith(`${path}/`);

  const destinations: Destination[] = [];
  const add = (destination: Omit<Destination, 'shortLabel'> & { shortLabel?: string }) =>
    destinations.push({ ...destination, shortLabel: destination.shortLabel ?? destination.label });
  if (member) {
    add({ key: 'home', to: dashboardPath(home.id), icon: 'dashboard', label: t('nav.dashboard'), shortLabel: t('nav.home'), primary: true, section: null, current: pathname === dashboardPath(home.id) });
    add({ key: 'files', to: filesPath(home.id), icon: 'file_copy', label: t('nav.files'), primary: true, section: null, current: under(filesPath(home.id)) || under(`/c/${home.id}/file`) });
  }
  if (inTeams) add({ key: 'files', to: filesPath(home.id), icon: 'file_copy', label: t('nav.files'), primary: true, section: null, current: under(filesPath(home.id)) || under(`/c/${home.id}/file`) });
  add({ key: 'shared', to: '/shared', icon: 'share', label: t('nav.shared'), primary: !member, section: null, current: under('/shared') });
  if (outsideRecords) {
    const crm = `/c/${home.id}/crm`;
    add({ key: 'deals', to: `${crm}/deals`, icon: 'handshake', label: t('crm.deals'), primary: true, section: 'customers', current: under(`${crm}/deals`) });
    add({ key: 'accounts', to: `${crm}/accounts`, icon: 'apartment', label: t('crm.accounts'), primary: false, section: 'customers', current: under(`${crm}/accounts`) });
    add({ key: 'contacts', to: `${crm}/contacts`, icon: 'contacts', label: t('crm.contacts'), primary: false, section: 'customers', current: under(`${crm}/contacts`) });
  }
  if (member) {
    const crm = `/c/${home.id}/crm`;
    add({ key: 'deals', to: `${crm}/deals`, icon: 'handshake', label: t('crm.deals'), primary: true, section: 'customers', current: under(`${crm}/deals`) });
    add({ key: 'accounts', to: `${crm}/accounts`, icon: 'apartment', label: t('crm.accounts'), primary: false, section: 'customers', current: under(`${crm}/accounts`) });
    add({ key: 'contacts', to: `${crm}/contacts`, icon: 'contacts', label: t('crm.contacts'), primary: false, section: 'customers', current: under(`${crm}/contacts`) });
    add({ key: 'tasks', to: `${crm}/tasks`, icon: 'check_box', label: t('crm.myTasks'), shortLabel: t('nav.tasks'), primary: true, section: 'customers', current: under(`${crm}/tasks`), badge: overdue });
    add({ key: 'calendar', to: `${crm}/calendar`, icon: 'calendar_month', label: t('crm.calendar'), primary: false, section: 'customers', current: under(`${crm}/calendar`) });
    add({ key: 'signing', to: `/c/${home.id}/signing`, icon: 'signature', label: t('nav.signing'), primary: false, section: 'company', current: under(`/c/${home.id}/signing`) });
    add({ key: 'trash', to: `/c/${home.id}/trash`, icon: 'delete', label: t('nav.trash'), primary: false, section: 'company', current: under(`/c/${home.id}/trash`) });
    add({ key: 'settings', to: `/c/${home.id}/settings/members`, icon: 'settings', label: t('nav.settings'), primary: false, section: 'company', current: under(`/c/${home.id}/settings`) });
  }
  return destinations;
}

/** Material's badge: a count over the icon, in the error colour; read out as words. */
function Badge({ count }: { count?: number }) {
  if (!count) return null;
  return (
    <span className="nav-badge" aria-hidden>
      {count > 999 ? '999+' : count}
    </span>
  );
}

/** A destination with a badge is named with the count in words ("My tasks, 2 overdue"). */
function useBadgeName() {
  const { t } = useI18n();
  return (label: string, count?: number) => (count ? `${label}, ${t('nav.overdue', { count })}` : undefined);
}

/** Whether an on-screen keyboard takes the bottom of the window (the bar then steps aside). */
function useOnScreenKeyboard(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const check = () => setOpen(window.innerHeight - viewport.height > 150);
    viewport.addEventListener('resize', check);
    return () => viewport.removeEventListener('resize', check);
  }, []);
  return open;
}

/** The phone's navigation bar: the main destinations and More. */
export function NavigationBar({ destinations, onMore, moreOpen }: { destinations: Destination[]; onMore: () => void; moreOpen: boolean }) {
  const { t } = useI18n();
  const keyboard = useOnScreenKeyboard();
  const badgeName = useBadgeName();
  const primary = destinations.filter((destination) => destination.primary);
  const rest = destinations.filter((destination) => !destination.primary);
  return (
    <nav className="nav-bar" aria-label={t('nav.main')} data-keyboard={keyboard || undefined}>
      {primary.map((destination) => (
        <Link key={destination.key} to={destination.to} className="nav-bar-item" aria-current={destination.current ? 'page' : undefined} aria-label={badgeName(destination.shortLabel, destination.badge)}>
          <span className="nav-indicator">
            <Icon name={destination.icon} filled={destination.current} />
            <Badge count={destination.badge} />
          </span>
          <span className="nav-label">{destination.shortLabel}</span>
        </Link>
      ))}
      {rest.length > 0 && (
        <button type="button" className="nav-bar-item" aria-haspopup="dialog" aria-expanded={moreOpen} data-current={rest.some((destination) => destination.current) || undefined} onClick={onMore}>
          <span className="nav-indicator">
            <Icon name="more_horiz" />
          </span>
          <span className="nav-label">{t('nav.more')}</span>
        </button>
      )}
    </nav>
  );
}

const SECTIONS: Destination['section'][] = [null, 'customers', 'company'];

/** The drawer's list: every destination, grouped under headings with dividers between. */
function DrawerList({ destinations, onNavigate }: { destinations: Destination[]; onNavigate?: () => void }) {
  const { t } = useI18n();
  const badgeName = useBadgeName();
  return (
    <>
      {SECTIONS.map((section) => {
        const items = destinations.filter((destination) => destination.section === section);
        if (items.length === 0) return null;
        return (
          <div key={section ?? 'main'} className="nav-section">
            {section && <h2 className="nav-heading">{t(section === 'customers' ? 'nav.crm' : 'nav.company')}</h2>}
            <ul className="nav-list">
              {items.map((destination) => (
                <li key={destination.key} data-primary={destination.primary || undefined}>
                  <Link to={destination.to} aria-current={destination.current ? 'page' : undefined} aria-label={badgeName(destination.label, destination.badge)} onClick={onNavigate}>
                    <span className="nav-indicator">
                      <Icon name={destination.icon} filled={destination.current} />
                      <Badge count={destination.badge} />
                    </span>
                    <span className="nav-label">
                      <span className="nav-label-long">{destination.label}</span>
                      {/* Only one of the two is displayed (the rail shows the short one), so the link is
                          named by what it shows. */}
                      <span className="nav-label-short">{destination.shortLabel}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}

/**
 * The rail (tablets, and a folded drawer) or the drawer (computers). The menu button folds and
 * unfolds the drawer on a computer, and opens the modal drawer on a tablet, where the rail shows the
 * main destinations only.
 */
export function NavigationSide({ destinations, onMenu, folded, foot }: { destinations: Destination[]; onMenu: () => void; folded: boolean; foot: ReactNode }) {
  const { t } = useI18n();
  return (
    <nav className="nav-side" aria-label={t('nav.main')} data-folded={folded || undefined}>
      <button type="button" className="icon-btn nav-menu" aria-label={t('nav.menu')} onClick={onMenu}>
        <Icon name="menu" />
      </button>
      <DrawerList destinations={destinations} />
      <div className="nav-foot">{foot}</div>
    </nav>
  );
}

/** Everything, in a drawer over the page: behind More on a phone and the menu button on a tablet. */
export function ModalDrawer({ destinations, open, onOpenChange, head, foot }: { destinations: Destination[]; open: boolean; onOpenChange: (open: boolean) => void; head: ReactNode; foot: ReactNode }) {
  const { t } = useI18n();
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="dialog-overlay" />
        <DialogPrimitive.Content className="nav-modal" aria-describedby={undefined}>
          <DialogPrimitive.Title className="visually-hidden">{t('nav.menu')}</DialogPrimitive.Title>
          <div className="nav-modal-head">
            {head}
            <DialogPrimitive.Close className="icon-btn" aria-label={t('common.close')}>
              <Icon name="close" />
            </DialogPrimitive.Close>
          </div>
          <nav aria-label={t('nav.menu')}>
            <DrawerList destinations={destinations} onNavigate={() => onOpenChange(false)} />
          </nav>
          <div className="nav-foot">{foot}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Whether the computer's drawer is folded to a rail: this person's choice, kept in the browser. */
export function useFoldedDrawer(): [boolean, () => void] {
  const [folded, setFolded] = useState(() => {
    try {
      return localStorage.getItem('teamfolio.drawerFolded') === 'true';
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setFolded((current) => {
      try {
        localStorage.setItem('teamfolio.drawerFolded', String(!current));
      } catch {
        // Private windows may refuse; the choice then lasts until the page is closed.
      }
      return !current;
    });
  return [folded, toggle];
}
